import type { ReactNode } from "react";
import { useContainerEventsSubscription } from "../api/queries";
import { EntitlementBanner } from "../features/billing/EntitlementBanner";
import { FirstLoginInterstitial } from "../features/sso/FirstLoginInterstitial";
import { NavSidebar } from "./NavSidebar";
import { TopBar } from "./TopBar";
import { useNavDrawer } from "./useNavDrawer";
import "./shell.css";

export function AppShell({ children }: { children: ReactNode }) {
  // Charter P-1: this is the one place the shell subscribes to the (today:
  // simulated) container event stream for the caller's tenant. Every
  // list/detail view underneath reads from the same react-query cache this
  // patches, so they update live without any view polling on its own. The
  // tenant comes from the session server-side (ADR-0035 M1 PR-D removed the
  // fleet switcher), so there is nothing to select or thread here.
  useContainerEventsSubscription();

  // Narrow-viewport nav drawer (Pass 1 responsive shell). No-op on desktop —
  // see useNavDrawer's doc comment for why toggling it there is harmless.
  const nav = useNavDrawer();

  return (
    <div className="shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <TopBar isNavOpen={nav.isOpen} onToggleNav={nav.toggle} />
      <EntitlementBanner />
      {/* Mounted once for the whole authenticated app, same reasoning as the
          event subscription above — see FirstLoginInterstitial's doc comment
          for why it must not re-trigger on ordinary in-app navigation. */}
      <FirstLoginInterstitial />
      <div className="shell__body">
        {/* Backdrop only ever visible below the mobile breakpoint (shell.css
            hides it outright above that width, regardless of isOpen) — dims
            the page and gives a click target to close the drawer. */}
        {nav.isOpen && <button type="button" className="shell__backdrop" aria-label="Close navigation" onClick={nav.close} />}
        <NavSidebar isOpen={nav.isOpen} />
        <main className="shell__content" id="main-content" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}
